import { AddressLink } from '../common/AddressLink';
import PendingTasksSection from './PendingTasksSection';
import AddAppointmentModal from './AddAppointmentModal';
import axios from 'axios';
import React, { useState, useEffect, useRef } from 'react';
import { apiClient, showToast } from '../../utils/apiClient';
import { API } from '../../config';
import { formatDate, formatDateTime, formatNotesWithBullets, getDayOfWeek, formatDateWithDay, getLocationDisplay, getLocationChipColor, getRowAlert, formatDatetimeForInput, parseUTCDatetime, getTodayInAEST, isPrepaymentApproaching, hasAppointments, getAppointmentSummary } from '../../utils/helpers';

const CalendarView = ({ onClose, initialView = 'grid', onAppointmentUpdate }) => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    // Get current date in local timezone
    const now = new Date();
    const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
    // Calculate days to subtract to get to Monday
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysToMonday);
    console.log('📅 Initial week start (Monday):', monday.toString(), 'dayOfWeek:', dayOfWeek, 'daysToMonday:', daysToMonday);
    return monday;
  });
  const [filter, setFilter] = useState('all'); // 'all', 'ashes', 'prepayments', 'viewing', 'other', 'external'
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showAddAppointment, setShowAddAppointment] = useState(false);
  const [activePets, setActivePets] = useState([]);
  const [editingAppointment, setEditingAppointment] = useState(false);
  const [editedDatetime, setEditedDatetime] = useState('');
  const [editedNotes, setEditedNotes] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [calendarView, setCalendarView] = useState(initialView); // 'grid' or 'timeslot' - use initial view from prop
  const [prefilledDatetime, setPrefilledDatetime] = useState(''); // For click-to-create from time slots

  useEffect(() => {
    fetchAppointments();
  }, [currentWeekStart, filter]);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const weekEnd = new Date(currentWeekStart.getFullYear(), currentWeekStart.getMonth(), currentWeekStart.getDate() + 6);

      // Format dates as YYYY-MM-DD in local timezone
      const formatLocalDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const startDateStr = formatLocalDate(currentWeekStart);
      const endDateStr = formatLocalDate(weekEnd);

      const response = await apiClient.get('/calendar/appointments', {
        params: {
          start_date: startDateStr,
          end_date: endDateStr,
          appointment_type: filter
        }
      });

      console.log('📅 Calendar appointments fetched:', response.data.appointments);
      console.log('📅 Week range:', startDateStr, 'to', endDateStr);
      setAppointments(response.data.appointments || []);
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEditedDatetime = async () => {
    try {
      if (!selectedAppointment) return;
      
      // Require datetime for external appointments, but allow notes-only edits for pet appointments
      if (selectedAppointment.type === 'external' && !editedDatetime) {
        showToast('Please enter a date and time', 'error');
        return;
      }
      
      if (selectedAppointment.type === 'external') {
        // Update external appointment
        await apiClient.put(`/external-appointments/${selectedAppointment.id}`, {
          datetime: editedDatetime,
          notes: editedNotes
        });
      } else {
        // Update pet record
        const updateData = {};
        
        // Helper to ensure datetime has seconds
        const addSecondsIfNeeded = (dt) => {
          if (!dt) return dt;
          return dt.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/) ? dt + ':00' : dt;
        };
        
        // Only update datetime if it was changed
        if (editedDatetime) {
          const dtWithSeconds = addSecondsIfNeeded(editedDatetime);
          if (selectedAppointment.type === 'ashes_collection') {
            updateData.ashes_collection_datetime = dtWithSeconds;
          } else if (selectedAppointment.type === 'prepayment_pickup') {
            updateData.prepayment_date = dtWithSeconds; // Full datetime for prepayments with time
          } else if (selectedAppointment.type === 'viewing_room') {
            updateData.viewing_room_datetime = dtWithSeconds;
          } else if (selectedAppointment.type === 'showroom_visit') {
            updateData.showroom_visit_datetime = dtWithSeconds;
          } else if (selectedAppointment.type === 'other_appointment') {
            updateData.other_appointment_datetime = dtWithSeconds;
          } else if (selectedAppointment.type === 'pet_delivery') {
            updateData.pet_delivery_datetime = dtWithSeconds;
          } else if (selectedAppointment.type === 'pet_dropoff') {
            updateData.pet_dropoff_datetime = dtWithSeconds;
          } else if (selectedAppointment.type === 'home_collection') {
            updateData.home_collection_datetime = dtWithSeconds;
          } else if (selectedAppointment.type === 'vet_dropoff') {
            updateData.vet_dropoff_datetime = dtWithSeconds;
          } else if (selectedAppointment.type === 'euthanasia') {
            updateData.euthanasia_datetime = dtWithSeconds;
          }
        }
        
        // Update notes based on appointment type
        if (selectedAppointment.type === 'ashes_collection') {
          updateData.ashes_collection_notes = editedNotes;
        } else if (selectedAppointment.type === 'prepayment_pickup') {
          updateData.prepayment_notes = editedNotes;
        } else if (selectedAppointment.type === 'viewing_room') {
          updateData.viewing_room_notes = editedNotes;
        } else if (selectedAppointment.type === 'showroom_visit') {
          updateData.showroom_visit_notes = editedNotes;
        } else if (selectedAppointment.type === 'other_appointment') {
          updateData.other_appointment_notes = editedNotes;
        } else if (selectedAppointment.type === 'pet_delivery') {
          updateData.pet_delivery_notes = editedNotes;
        } else if (selectedAppointment.type === 'pet_dropoff') {
          updateData.pet_dropoff_notes = editedNotes;
        } else if (selectedAppointment.type === 'home_collection') {
          updateData.home_collection_notes = editedNotes;
        } else if (selectedAppointment.type === 'vet_dropoff') {
          updateData.vet_dropoff_notes = editedNotes;
        }
        
        await apiClient.put(`/pets/${selectedAppointment.id}`, updateData);
      }
      
      showToast('Appointment updated successfully! ✅');
      setEditingAppointment(false);
      setEditedNotes('');
      setSelectedAppointment(null);
      fetchAppointments();
      
      // Refresh pet cards on dashboard to show updated appointment badges
      if (onAppointmentUpdate) {
        onAppointmentUpdate();
      }
      
    } catch (error) {
      console.error('Failed to update appointment:', error);
      showToast('Failed to update appointment', 'error');
    }
  };

  const handleCompleteAppointment = async () => {
    try {
      if (!selectedAppointment) return;
      
      if (selectedAppointment.type === 'external') {
        // Mark external appointment as completed
        await apiClient.put(`/external-appointments/${selectedAppointment.id}`, {
          status: 'completed'
        });
      } else {
        // Mark pet appointment as completed
        const updateData = {};
        
        if (selectedAppointment.type === 'ashes_collection') {
          updateData.ashes_collection_completed = true;
        } else if (selectedAppointment.type === 'prepayment_pickup') {
          updateData.prepayment_completed = true;
        } else if (selectedAppointment.type === 'viewing_room') {
          // Viewing room - clear all fields (no history needed)
          updateData.viewing_room = null;
          updateData.viewing_room_datetime = null;
          updateData.viewing_room_notes = null;
        } else if (selectedAppointment.type === 'showroom_visit') {
          // Showroom visit - clear all fields (no history needed)
          updateData.showroom_visit_datetime = null;
          updateData.showroom_visit_notes = null;
          updateData.showroom_visit_completed = null;
          console.log('🚙 Completing Showroom visit, clearing fields:', updateData);
        } else if (selectedAppointment.type === 'other_appointment') {
          // Other appointment - clear all fields (no history needed)
          updateData.other_appointment_datetime = null;
          updateData.other_appointment_notes = null;
          updateData.other_appointment_completed = null;
          console.log('🎗️ Completing Other appointment, clearing fields:', updateData);
        } else if (selectedAppointment.type === 'pet_delivery') {
          updateData.pet_delivery_completed = true;
          console.log('🚚 Completing Ashes Delivery');
        } else if (selectedAppointment.type === 'pet_dropoff') {
          updateData.pet_dropoff_completed = true;
          console.log('🦮 Completing Client Drop off');
        } else if (selectedAppointment.type === 'home_collection') {
          updateData.home_collection_completed = true;
          console.log('🏡 Completing Home Collection');
        } else if (selectedAppointment.type === 'vet_dropoff') {
          updateData.vet_dropoff_completed = true;
          console.log('🏥 Completing Vet Drop off');
        } else if (selectedAppointment.type === 'euthanasia') {
          updateData.euthanasia_completed = true;
          console.log('🕊️ Completing Euthanasia');
        }
        
        console.log('📤 Sending update to backend:', updateData);
        await apiClient.put(`/pets/${selectedAppointment.id}`, updateData);
        console.log('✅ Update successful');
      }
      
      showToast('Appointment marked as complete! ✅');
      setSelectedAppointment(null);
      fetchAppointments();
      
      // Refresh pet cards on dashboard
      if (onAppointmentUpdate) {
        onAppointmentUpdate();
      }
      
    } catch (error) {
      console.error('Failed to complete appointment:', error);
      showToast('Failed to complete appointment', 'error');
    }
  };

  const handleUndoComplete = async () => {
    try {
      if (!selectedAppointment) return;
      
      if (selectedAppointment.type === 'external') {
        // Mark external appointment as scheduled again
        await apiClient.put(`/external-appointments/${selectedAppointment.id}`, {
          status: 'scheduled'
        });
      } else {
        // Undo completion for pet appointment
        const updateData = {};
        
        if (selectedAppointment.type === 'ashes_collection') {
          updateData.ashes_collection_completed = false;
        } else if (selectedAppointment.type === 'prepayment_pickup') {
          updateData.prepayment_completed = false;
        } else if (selectedAppointment.type === 'showroom_visit') {
          updateData.showroom_visit_completed = false;
        } else if (selectedAppointment.type === 'other_appointment') {
          updateData.other_appointment_completed = false;
        } else if (selectedAppointment.type === 'pet_delivery') {
          updateData.pet_delivery_completed = false;
        }
        // Note: viewing room can't be undone since it's fully cleared
        
        await apiClient.put(`/pets/${selectedAppointment.id}`, updateData);
      }
      
      showToast('Appointment completion undone! ↩️');
      setSelectedAppointment(null);
      fetchAppointments();
      
      // Refresh pet cards on dashboard
      if (onAppointmentUpdate) {
        onAppointmentUpdate();
      }
      
    } catch (error) {
      console.error('Failed to undo completion:', error);
      showToast('Failed to undo completion', 'error');
    }
  };

  const handleDeleteAppointment = async () => {
    try {
      if (!selectedAppointment) return;
      
      if (selectedAppointment.type === 'external') {
        // Delete external appointment
        await apiClient.delete(`/external-appointments/${selectedAppointment.id}`);
      } else {
        // Clear appointment fields from pet record
        const updateData = {};
        
        if (selectedAppointment.type === 'ashes_collection') {
          updateData.ashes_collection_datetime = null;
          updateData.ashes_collection_booked = false;
        } else if (selectedAppointment.type === 'prepayment_pickup') {
          updateData.prepayment_date = null;
        } else if (selectedAppointment.type === 'viewing_room') {
          updateData.viewing_room_datetime = null;
          updateData.viewing_room = null;
        } else if (selectedAppointment.type === 'showroom_visit') {
          updateData.showroom_visit_datetime = null;
        } else if (selectedAppointment.type === 'other_appointment') {
          updateData.other_appointment_datetime = null;
          updateData.other_appointment_notes = null;
          console.log('🎗️ Deleting Other appointment, clearing fields:', updateData);
        } else if (selectedAppointment.type === 'pet_delivery') {
          updateData.pet_delivery_datetime = null;
          updateData.pet_delivery_notes = null;
          updateData.pet_delivery_completed = null;
          console.log('🚚 Deleting Ashes Delivery, clearing fields:', updateData);
        } else if (selectedAppointment.type === 'pet_dropoff') {
          updateData.pet_dropoff_datetime = null;
          updateData.pet_dropoff_notes = null;
          updateData.pet_dropoff_completed = null;
          console.log('🦮 Deleting Client Drop off, clearing fields:', updateData);
        } else if (selectedAppointment.type === 'home_collection') {
          updateData.home_collection_datetime = null;
          updateData.home_collection_notes = null;
          updateData.home_collection_completed = null;
          console.log('🏡 Deleting Home Collection, clearing fields:', updateData);
        } else if (selectedAppointment.type === 'vet_dropoff') {
          updateData.vet_dropoff_datetime = null;
          updateData.vet_dropoff_notes = null;
          updateData.vet_dropoff_completed = null;
          console.log('🏥 Deleting Vet Drop off, clearing fields:', updateData);
        } else if (selectedAppointment.type === 'euthanasia') {
          updateData.euthanasia_datetime = null;
          updateData.euthanasia_notes = null;
          updateData.euthanasia_completed = null;
          console.log('🕊️ Deleting Euthanasia, clearing fields:', updateData);
        }
        
        // Clear appointment notes
        updateData.appointment_notes = null;
        
        console.log('📤 Sending delete update to backend:', updateData);
        await apiClient.put(`/pets/${selectedAppointment.id}`, updateData);
        console.log('✅ Delete successful');
      }
      
      showToast('Appointment deleted successfully! ✅');
      setShowDeleteConfirm(false);
      setSelectedAppointment(null);
      fetchAppointments();
      
      // Refresh pet cards on dashboard
      if (onAppointmentUpdate) {
        onAppointmentUpdate();
      }
      
    } catch (error) {
      console.error('Failed to delete appointment:', error);
      showToast('Failed to delete appointment', 'error');
    }
  };

  const navigateWeek = (direction) => {
    const newWeekStart = new Date(currentWeekStart.getFullYear(), currentWeekStart.getMonth(), currentWeekStart.getDate() + (direction * 7));
    setCurrentWeekStart(newWeekStart);
  };

  const getAppointmentsForDay = (dayOffset) => {
    const targetDate = new Date(currentWeekStart.getFullYear(), currentWeekStart.getMonth(), currentWeekStart.getDate() + dayOffset);
    // Format as YYYY-MM-DD in local timezone
    const targetYear = targetDate.getFullYear();
    const targetMonth = String(targetDate.getMonth() + 1).padStart(2, '0');
    const targetDay = String(targetDate.getDate()).padStart(2, '0');
    const targetDateStr = `${targetYear}-${targetMonth}-${targetDay}`;

    const dayApts = appointments.filter(apt => {
      // Extract date part from datetime (handles both ISO strings and Date objects)
      const aptDateStr = apt.datetime.split('T')[0];
      const matches = aptDateStr === targetDateStr;
      console.log(`📅 Day ${dayOffset}: Target=${targetDateStr}, Apt=${apt.pet_name} ${aptDateStr}, Match=${matches}`);
      return matches;
    }).sort((a, b) => new Date(a.datetime) - new Date(b.datetime));
    
    console.log(`📅 Day ${dayOffset} (${targetDateStr}): ${dayApts.length} appointments`);
    return dayApts;
  };

  const isToday = (dayOffset) => {
    const targetDate = new Date(currentWeekStart.getFullYear(), currentWeekStart.getMonth(), currentWeekStart.getDate() + dayOffset);
    const today = new Date();
    return targetDate.toDateString() === today.toDateString();
  };

  const isPast = (dayOffset) => {
    const targetDate = new Date(currentWeekStart.getFullYear(), currentWeekStart.getMonth(), currentWeekStart.getDate() + dayOffset);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return targetDate < today;
  };

  const getDayName = (dayOffset) => {
    const targetDate = new Date(currentWeekStart.getFullYear(), currentWeekStart.getMonth(), currentWeekStart.getDate() + dayOffset);
    return targetDate.toLocaleDateString('en-AU', { weekday: 'short' });
  };

  const getDayNumber = (dayOffset) => {
    const targetDate = new Date(currentWeekStart.getFullYear(), currentWeekStart.getMonth(), currentWeekStart.getDate() + dayOffset);
    return targetDate.getDate();
  };

  const getWeekDisplay = () => {
    const weekEnd = new Date(currentWeekStart.getFullYear(), currentWeekStart.getMonth(), currentWeekStart.getDate() + 6);
    
    if (currentWeekStart.getMonth() === weekEnd.getMonth()) {
      return `${currentWeekStart.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })}`;
    } else {
      return `${currentWeekStart.toLocaleDateString('en-AU', { month: 'short' })} - ${weekEnd.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })}`;
    }
  };

  return (
    <div className="fixed inset-0 z-50">
      {/* Main Calendar Modal */}
      <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
        <div className="w-full max-w-7xl h-[95vh] flex flex-col">
          <div className="bg-white rounded-xl shadow-2xl flex flex-col h-full overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-teal-500 to-blue-500 text-white p-4 rounded-t-xl flex-shrink-0">
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center space-x-2 md:space-x-4 flex-1 min-w-0">
                  <span className="text-2xl md:text-4xl">📅</span>
                  <div className="min-w-0">
                    <h2 className="text-lg md:text-xl font-bold truncate">Appointment Calendar</h2>
                    <p className="text-teal-100 text-xs truncate">{getWeekDisplay()}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 flex-shrink-0">
                  <button
                    onClick={() => setShowAddAppointment(true)}
                    className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-3 py-1.5 rounded-lg font-medium flex items-center space-x-1 text-sm"
                  >
                    <span className="text-xl">+</span>
                    <span className="hidden sm:inline">Add</span>
                  </button>
                  <button
                    onClick={onClose}
                    className="text-white hover:text-gray-200 text-3xl font-bold leading-none"
                  >
                    ×
                  </button>
                </div>
              </div>
              
              {/* Filter & Navigation */}
              <div className="mt-2 flex flex-col md:flex-row justify-between items-start md:items-center space-y-2 md:space-y-0">
                {/* Week Navigation */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => navigateWeek(-1)}
                    className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg font-medium"
                  >
                    ← Prev Week
                  </button>
                  <button
                    onClick={() => setCurrentWeekStart(() => {
                      const now = new Date();
                      const dayOfWeek = now.getDay();
                      const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
                      const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysToMonday);
                      return monday;
                    })}
                    className="px-4 py-2 bg-white text-teal-600 hover:bg-opacity-90 rounded-lg font-medium"
                  >
                    Today
                  </button>
                  <button
                    onClick={() => navigateWeek(1)}
                    className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg font-medium"
                  >
                    Next Week →
                  </button>
                </div>
                
                {/* Filter Buttons */}
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => setFilter('all')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap ${
                      filter === 'all' 
                        ? 'bg-white text-teal-600' 
                        : 'bg-white bg-opacity-20 hover:bg-opacity-30'
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setFilter('ashes')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap ${
                      filter === 'ashes' 
                        ? 'bg-white text-blue-600' 
                        : 'bg-white bg-opacity-20 hover:bg-opacity-30'
                    }`}
                  >
                    🏺 Ashes Collection
                  </button>
                  <button
                    onClick={() => setFilter('prepayments')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap ${
                      filter === 'prepayments' 
                        ? 'bg-white text-orange-600' 
                        : 'bg-white bg-opacity-20 hover:bg-opacity-30'
                    }`}
                  >
                    ⭐️ Prepayments
                  </button>
                  <button
                    onClick={() => setFilter('viewing')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap ${
                      filter === 'viewing' 
                        ? 'bg-white text-purple-600' 
                        : 'bg-white bg-opacity-20 hover:bg-opacity-30'
                    }`}
                  >
                    🕯️ Viewing
                  </button>
                  <button
                    onClick={() => setFilter('other')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap ${
                      filter === 'other' 
                        ? 'bg-white text-pink-600' 
                        : 'bg-white bg-opacity-20 hover:bg-opacity-30'
                    }`}
                  >
                    🚐 Vet Collection
                  </button>
                  <button
                    onClick={() => setFilter('pet_delivery')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap ${
                      filter === 'pet_delivery' 
                        ? 'bg-white text-green-600' 
                        : 'bg-white bg-opacity-20 hover:bg-opacity-30'
                    }`}
                  >
                    🚚 Ashes Delivery
                  </button>
                  <button
                    onClick={() => setFilter('pet_dropoff')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap ${
                      filter === 'pet_dropoff' 
                        ? 'bg-white text-indigo-600' 
                        : 'bg-white bg-opacity-20 hover:bg-opacity-30'
                    }`}
                  >
                    🦮 Client Drop off
                  </button>
                  <button
                    onClick={() => setFilter('home_collection')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap ${
                      filter === 'home_collection' 
                        ? 'bg-white text-emerald-600' 
                        : 'bg-white bg-opacity-20 hover:bg-opacity-30'
                    }`}
                  >
                    🏡 Home Collection
                  </button>
                  <button
                    onClick={() => setFilter('vet_dropoff')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap ${
                      filter === 'vet_dropoff' 
                        ? 'bg-white text-orange-600' 
                        : 'bg-white bg-opacity-20 hover:bg-opacity-30'
                    }`}
                  >
                    🏥 Vet Drop off
                  </button>
                  <button
                    onClick={() => setFilter('euthanasia')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap ${
                      filter === 'euthanasia' 
                        ? 'bg-white text-rose-600' 
                        : 'bg-white bg-opacity-20 hover:bg-opacity-30'
                    }`}
                  >
                    🕊️ Euthanasia
                  </button>
                  <button
                    onClick={() => setFilter('external')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap ${
                      filter === 'external' 
                        ? 'bg-white text-gray-700' 
                        : 'bg-white bg-opacity-20 hover:bg-opacity-30'
                    }`}
                  >
                    👫 External
                  </button>
                  
                  {/* View Toggle - Desktop Only */}
                  <div className="hidden md:flex items-center ml-4 border-l border-white border-opacity-30 pl-4">
                    <button
                      onClick={() => setCalendarView('grid')}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap ${
                        calendarView === 'grid'
                          ? 'bg-white text-teal-600'
                          : 'bg-white bg-opacity-20 hover:bg-opacity-30'
                      }`}
                    >
                      📋 Week View
                    </button>
                    <button
                      onClick={() => setCalendarView('timeslot')}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap ml-2 ${
                        calendarView === 'timeslot'
                          ? 'bg-white text-teal-600'
                          : 'bg-white bg-opacity-20 hover:bg-opacity-30'
                      }`}
                    >
                      🕐 Time View
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Calendar Grid/List - Responsive */}
            <div className="flex-1 overflow-y-auto p-4">
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
                  <p className="text-gray-600 mt-4">Loading appointments...</p>
                </div>
              ) : (
                <>
                  {/* Desktop: Time-Slot Grid View */}
                  {calendarView === 'timeslot' && (
                    <div className="hidden md:block overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr>
                            <th className="border border-gray-300 bg-gray-100 p-2 text-xs font-bold sticky left-0 z-10 w-20">TIME</th>
                            {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day, i) => {
                              const dayDate = new Date(currentWeekStart.getFullYear(), currentWeekStart.getMonth(), currentWeekStart.getDate() + i);
                              const isTodayCell = isToday(i);
                              return (
                                <th key={day} className={`border border-gray-300 p-2 text-center min-w-[140px] ${
                                  isTodayCell ? 'bg-teal-100' : 'bg-gray-100'
                                }`}>
                                  <div className={`text-xs font-bold ${isTodayCell ? 'text-teal-800' : 'text-gray-700'}`}>{day.substring(0, 3).toUpperCase()}</div>
                                  <div className={`text-lg font-bold ${isTodayCell ? 'text-teal-600' : 'text-gray-900'}`}>{getDayNumber(i)}</div>
                                </th>
                              );
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {/* Generate time slots from 8:00 AM to 5:00 PM (30-minute intervals) */}
                          {Array.from({ length: 16 }, (_, i) => {
                            const hour = Math.floor(i / 2) + 8;
                            const minute = i % 2 === 0 ? '00' : '30';
                            const timeLabel = `${hour > 12 ? hour - 12 : hour}:${minute} ${hour >= 12 ? 'PM' : 'AM'}`;
                            const timeValue = `${String(hour).padStart(2, '0')}:${minute}`;
                            
                            return (
                              <tr key={timeValue} className="h-10">
                                <td className="border border-gray-300 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600 sticky left-0 z-10">
                                  {timeLabel}
                                </td>
                                {[0, 1, 2, 3, 4, 5, 6].map((dayOffset) => {
                                  // Find appointments for this day and time slot
                                  const dayAppointments = getAppointmentsForDay(dayOffset);
                                  const slotAppointments = dayAppointments.filter(apt => {
                                    const aptDate = new Date(apt.datetime);
                                    const aptHour = aptDate.getHours();
                                    const aptMinute = aptDate.getMinutes();
                                    const aptTime = `${String(aptHour).padStart(2, '0')}:${aptMinute < 30 ? '00' : '30'}`;
                                    return aptTime === timeValue;
                                  });

                                  const isTodayCell = isToday(dayOffset);
                                  
                                  // Create datetime for this slot
                                  const slotDate = new Date(currentWeekStart.getFullYear(), currentWeekStart.getMonth(), currentWeekStart.getDate() + dayOffset);
                                  slotDate.setHours(hour);
                                  slotDate.setMinutes(minute === '00' ? 0 : 30);
                                  const slotDatetime = formatDatetimeForInput(slotDate);
                                  
                                  return (
                                    <td 
                                      key={dayOffset} 
                                      className={`border border-gray-300 p-1 align-top cursor-pointer hover:bg-teal-100 transition-colors ${
                                        isTodayCell ? 'bg-teal-50' : 'bg-white'
                                      }`}
                                      onClick={() => {
                                        // Open add appointment modal with pre-filled time
                                        setPrefilledDatetime(slotDatetime);
                                        setShowAddAppointment(true);
                                      }}
                                      title="Click to add appointment"
                                    >
                                      {slotAppointments.map(apt => {
                                        let bgColor, icon, typeLabel;
                                        if (apt.type === 'viewing_room') {
                                          bgColor = 'bg-purple-100 border-purple-400';
                                          icon = '🕯️';
                                          typeLabel = 'Viewing Room';
                                        } else if (apt.type === 'ashes_collection') {
                                          bgColor = 'bg-blue-100 border-blue-400';
                                          icon = '🏺';
                                          typeLabel = 'Ashes Collection';
                                        } else if (apt.type === 'showroom_visit') {
                                          bgColor = 'bg-teal-100 border-teal-400';
                                          icon = '🚙';
                                          typeLabel = 'Showroom Visit';
                                        } else if (apt.type === 'other_appointment') {
                                          bgColor = 'bg-pink-100 border-pink-400';
                                          icon = '🚐';
                                          typeLabel = 'Vet Collection';
                                        } else if (apt.type === 'home_collection') {
                                          bgColor = 'bg-emerald-100 border-emerald-400';
                                          icon = '🏡';
                                          typeLabel = 'Home Collection';
                                        } else if (apt.type === 'vet_dropoff') {
                                          bgColor = 'bg-orange-100 border-orange-400';
                                          icon = '🏥';
                                          typeLabel = 'Vet Drop off';
                                        } else if (apt.type === 'euthanasia') {
                                          bgColor = 'bg-rose-100 border-rose-400';
                                          icon = '🕊️';
                                          typeLabel = 'Euthanasia';
                                        } else if (apt.type === 'external') {
                                          bgColor = 'bg-gray-200 border-gray-400';
                                          icon = '👫';
                                          typeLabel = apt.appointment_type || 'External';
                                        } else {
                                          bgColor = 'bg-orange-100 border-orange-400';
                                          icon = '⭐️';
                                          typeLabel = 'Prepayment Pickup';
                                        }

                                        return (
                                          <button
                                            key={apt.id}
                                            onClick={(e) => {
                                              e.stopPropagation(); // Prevent td click
                                              setSelectedAppointment(apt);
                                            }}
                                            className={`w-full text-left p-2 rounded border-2 ${bgColor} hover:shadow-md text-xs mb-1`}
                                          >
                                            <div className="font-bold truncate">{icon} {apt.pet_name || apt.contact_name}</div>
                                            <div className="text-gray-700 truncate text-xs font-medium">{typeLabel}</div>
                                            <div className="text-gray-600 truncate text-xs">{apt.client_name || apt.contact_name}</div>
                                            {apt.notes && apt.notes.trim() && (
                                              <div className="text-gray-600 italic text-xs mt-1 border-t border-gray-300 pt-1">💬 {apt.notes}</div>
                                            )}
                                          </button>
                                        );
                                      })}
                                      {slotAppointments.length === 0 && (
                                        <div className="text-xs text-gray-300 text-center py-2">
                                          +
                                        </div>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                  
                  {/* Desktop: Weekly Grid (Original View) */}
                  {calendarView === 'grid' && (
                  <div className="hidden lg:grid md:grid-cols-7 gap-2">
                    {/* Day Headers */}
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, i) => (
                      <div key={day} className={`text-center font-semibold p-3 rounded-t-lg ${
                        isToday(i) ? 'bg-teal-100 text-teal-800' : 'bg-gray-100 text-gray-700'
                      }`}>
                        <div className="text-xs">{day}</div>
                        <div className="text-lg">{getDayNumber(i)}</div>
                      </div>
                    ))}
                    
                    {/* Day Cells */}
                    {[0, 1, 2, 3, 4, 5, 6].map((dayOffset) => {
                      const dayAppointments = getAppointmentsForDay(dayOffset);
                      const isTodayCell = isToday(dayOffset);
                      const isPastCell = isPast(dayOffset);
                      
                      return (
                        <div
                          key={dayOffset}
                          className={`min-h-[200px] border-2 rounded-lg p-2 ${
                            isTodayCell 
                              ? 'border-teal-400 bg-teal-50 shadow-lg animate-pulse' 
                              : isPastCell
                              ? 'border-gray-200 bg-gray-50'
                              : 'border-gray-300 bg-white'
                          }`}
                        >
                          <div className="space-y-2">
                            {dayAppointments.map((apt) => {
                              // Consistent colors - same as mobile
                              let bgColor, borderColor, icon, displayName;
                              if (apt.type === 'viewing_room') {
                                bgColor = 'bg-purple-50';
                                borderColor = 'border-purple-300 hover:bg-purple-100';
                                icon = '🕯️';
                                displayName = apt.pet_name;
                              } else if (apt.type === 'ashes_collection') {
                                bgColor = 'bg-blue-50';
                                borderColor = 'border-blue-300 hover:bg-blue-100';
                                icon = '🏺';
                                displayName = apt.pet_name;
                              } else if (apt.type === 'showroom_visit') {
                                bgColor = 'bg-teal-50';
                                borderColor = 'border-teal-300 hover:bg-teal-100';
                                icon = '🚙';
                                displayName = apt.pet_name;
                              } else if (apt.type === 'other_appointment') {
                                bgColor = 'bg-pink-50';
                                borderColor = 'border-pink-300 hover:bg-pink-100';
                                icon = '🎗️';
                                displayName = apt.pet_name;
                              } else if (apt.type === 'pet_delivery') {
                                bgColor = 'bg-green-50';
                                borderColor = 'border-green-300 hover:bg-green-100';
                                icon = '🚚';
                                displayName = apt.pet_name;
                              } else if (apt.type === 'pet_dropoff') {
                                bgColor = 'bg-indigo-50';
                                borderColor = 'border-indigo-300 hover:bg-indigo-100';
                                icon = '🦮';
                                displayName = apt.pet_name;
                              } else if (apt.type === 'home_collection') {
                                bgColor = 'bg-emerald-50';
                                borderColor = 'border-emerald-300 hover:bg-emerald-100';
                                icon = '🏡';
                                displayName = apt.pet_name;
                              } else if (apt.type === 'vet_dropoff') {
                                bgColor = 'bg-orange-50';
                                borderColor = 'border-orange-300 hover:bg-orange-100';
                                icon = '🏥';
                                displayName = apt.pet_name;
                              } else if (apt.type === 'euthanasia') {
                                bgColor = 'bg-rose-50';
                                borderColor = 'border-rose-300 hover:bg-rose-100';
                                icon = '🕊️';
                                displayName = apt.pet_name;
                              } else if (apt.type === 'external') {
                                bgColor = 'bg-gray-100';
                                borderColor = 'border-gray-400 hover:bg-gray-200';
                                icon = '👫';
                                displayName = apt.pet_name || apt.contact_name;
                              } else {
                                bgColor = 'bg-orange-50';
                                borderColor = 'border-orange-300 hover:bg-orange-100';
                                icon = '⭐️';
                                displayName = apt.pet_name;
                              }
                              
                              // Get appointment type label
                              const getTypeLabel = () => {
                                if (apt.type === 'ashes_collection') return 'Ashes Collection';
                                if (apt.type === 'prepayment_pickup') return 'Prepayment';
                                if (apt.type === 'viewing_room') return 'Viewing Room';
                                if (apt.type === 'showroom_visit') return 'Showroom Visit';
                                if (apt.type === 'other_appointment') return 'Vet Collection';
                                if (apt.type === 'pet_delivery') return 'Ashes Delivery';
                                if (apt.type === 'pet_dropoff') return 'Client Drop off';
                                if (apt.type === 'home_collection') return 'Home Collection';
                                if (apt.type === 'vet_dropoff') return 'Vet Drop off';
                                if (apt.type === 'euthanasia') return 'Euthanasia';
                                if (apt.type === 'external') {
                                  return apt.appointment_type || 'External Visit';
                                }
                                return '';
                              };
                              
                              return (
                                <button
                                  key={apt.id}
                                  onClick={() => setSelectedAppointment(apt)}
                                  className={`w-full text-left p-2 rounded-lg border-2 hover:shadow-md transition-all ${bgColor} ${borderColor}`}
                                >
                                  <div className="text-xs font-bold truncate">
                                    {icon} {displayName}
                                  </div>
                                  <div className={`text-xs font-medium truncate ${
                                    apt.type === 'viewing_room' ? 'text-purple-700' :
                                    apt.type === 'ashes_collection' ? 'text-blue-700' :
                                    apt.type === 'showroom_visit' ? 'text-teal-700' :
                                    apt.type === 'other_appointment' ? 'text-pink-700' :
                                    apt.type === 'pet_delivery' ? 'text-green-700' :
                                    apt.type === 'pet_dropoff' ? 'text-indigo-700' :
                                    apt.type === 'home_collection' ? 'text-emerald-700' :
                                    apt.type === 'vet_dropoff' ? 'text-orange-700' :
                                    apt.type === 'euthanasia' ? 'text-rose-700' :
                                    apt.type === 'external' ? 'text-gray-700' :
                                    'text-orange-700'
                                  }`}>
                                    {apt.type === 'ashes_collection' ? 'Ashes Collection' :
                                     apt.type === 'prepayment_pickup' ? 'Prepayment' :
                                     apt.type === 'viewing_room' ? 'Viewing Room' :
                                     apt.type === 'showroom_visit' ? 'Showroom Visit' :
                                     apt.type === 'other_appointment' ? 'Vet Collection' :
                                     apt.type === 'pet_delivery' ? 'Ashes Delivery' :
                                     apt.type === 'pet_dropoff' ? 'Client Drop off' :
                                     apt.type === 'home_collection' ? 'Home Collection' :
                                     apt.type === 'euthanasia' ? 'Euthanasia' :
                                     apt.type === 'external' ? (apt.appointment_type === 'Vet Collection' && apt.other_type_description ? apt.other_type_description : (apt.appointment_type || 'External')) :
                                     'Appointment'}
                                  </div>
                                  <div className="text-xs text-gray-500 truncate">{apt.type === 'external' ? apt.contact_name : apt.client_name}</div>
                                  <div className="text-xs font-medium mt-1">
                                    {new Date(apt.datetime).toLocaleTimeString('en-AU', { 
                                      hour: 'numeric', 
                                      minute: '2-digit', 
                                      hour12: true 
                                    })}
                                    {apt.duration && ` (${apt.duration.replace(' minutes', 'min')})`}
                                  </div>
                                  {apt.notes && (
                                    <div className="text-xs text-gray-600 italic mt-1 truncate">
                                      💬 {apt.notes}
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                            
                            {dayAppointments.length === 0 && (
                              <div className="text-xs text-gray-400 text-center pt-8">
                                No appointments
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  )}
                  
                  {/* Mobile: List View */}
                  <div className="lg:hidden space-y-3">
                    {[0, 1, 2, 3, 4, 5, 6].map((dayOffset) => {
                      const dayAppointments = getAppointmentsForDay(dayOffset);
                      if (dayAppointments.length === 0) return null;
                      
                      const isTodayCell = isToday(dayOffset);
                      const targetDate = new Date(currentWeekStart.getFullYear(), currentWeekStart.getMonth(), currentWeekStart.getDate() + dayOffset);
                      
                      return (
                        <div key={dayOffset} className="bg-white rounded-lg shadow-sm border">
                          {/* Day Header */}
                          <div className={`p-3 font-semibold ${
                            isTodayCell ? 'bg-teal-100 text-teal-800' : 'bg-gray-100 text-gray-700'
                          }`}>
                            {targetDate.toLocaleDateString('en-AU', { 
                              weekday: 'long', 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                          </div>
                          
                          {/* Appointments List */}
                          <div className="p-3 space-y-2">
                            {dayAppointments.map((apt) => {
                              const aptTime = new Date(apt.datetime);
                              const now = new Date();
                              // Don't apply overdue/upcoming warnings on mobile list view
                              
                              let bgColor, borderColor, icon, displayName, subtitle;
                              if (apt.type === 'viewing_room') {
                                bgColor = 'bg-purple-50';
                                borderColor = 'border-purple-300';
                                icon = '🕯️';
                                displayName = apt.pet_name;
                                subtitle = apt.client_name;
                              } else if (apt.type === 'ashes_collection') {
                                bgColor = 'bg-blue-50';
                                borderColor = 'border-blue-300';
                                icon = '🏺';
                                displayName = apt.pet_name;
                                subtitle = apt.client_name;
                              } else if (apt.type === 'showroom_visit') {
                                bgColor = 'bg-teal-50';
                                borderColor = 'border-teal-300';
                                icon = '🚙';
                                displayName = apt.pet_name;
                                subtitle = apt.client_name;
                              } else if (apt.type === 'other_appointment') {
                                bgColor = 'bg-pink-50';
                                borderColor = 'border-pink-300';
                                icon = '🎗️';
                                displayName = apt.pet_name;
                                subtitle = apt.client_name;
                              } else if (apt.type === 'pet_delivery') {
                                bgColor = 'bg-green-50';
                                borderColor = 'border-green-300';
                                icon = '🚚';
                                displayName = apt.pet_name;
                                subtitle = apt.client_name;
                              } else if (apt.type === 'pet_dropoff') {
                                bgColor = 'bg-indigo-50';
                                borderColor = 'border-indigo-300';
                                icon = '🦮';
                                displayName = apt.pet_name;
                                subtitle = apt.client_name;
                              } else if (apt.type === 'home_collection') {
                                bgColor = 'bg-emerald-50';
                                borderColor = 'border-emerald-300';
                                icon = '🏡';
                                displayName = apt.pet_name;
                                subtitle = apt.client_name;
                              } else if (apt.type === 'vet_dropoff') {
                                bgColor = 'bg-orange-50';
                                borderColor = 'border-orange-300';
                                icon = '🏥';
                                displayName = apt.pet_name;
                                subtitle = apt.client_name;
                              } else if (apt.type === 'euthanasia') {
                                bgColor = 'bg-rose-50';
                                borderColor = 'border-rose-300';
                                icon = '🕊️';
                                displayName = apt.pet_name;
                                subtitle = apt.client_name;
                              } else if (apt.type === 'external') {
                                bgColor = 'bg-gray-100';
                                borderColor = 'border-gray-400';
                                icon = '👫';
                                displayName = apt.pet_name || apt.contact_name;
                                subtitle = apt.pet_name ? apt.contact_name : apt.appointment_type;
                              } else {
                                bgColor = 'bg-orange-50';
                                borderColor = 'border-orange-300';
                                icon = '⭐️';
                                displayName = apt.pet_name;
                                subtitle = apt.client_name;
                              }
                              
                              return (
                                <button
                                  key={apt.id}
                                  onClick={() => setSelectedAppointment(apt)}
                                  className={`w-full text-left p-4 rounded-lg border-2 ${bgColor} ${borderColor} active:scale-95 transition-transform`}
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="font-bold text-gray-900 text-base mb-1">
                                        {icon} {displayName}
                                      </div>
                                      <div className="text-sm text-gray-600 mb-2">{subtitle}</div>
                                      <div className="text-sm font-medium text-gray-900">
                                        🕐 {new Date(apt.datetime).toLocaleTimeString('en-AU', { 
                                          hour: 'numeric', 
                                          minute: '2-digit', 
                                          hour12: true 
                                        })}
                                        {apt.duration && <span className="text-purple-600"> ({apt.duration})</span>}
                                      </div>
                                      {/* Appointment Type Label */}
                                      <div className={`text-xs font-medium mt-1 ${
                                        apt.type === 'viewing_room' ? 'text-purple-600' :
                                        apt.type === 'ashes_collection' ? 'text-blue-600' :
                                        apt.type === 'showroom_visit' ? 'text-teal-600' :
                                        apt.type === 'other_appointment' ? 'text-pink-600' :
                                        apt.type === 'pet_delivery' ? 'text-green-600' :
                                        apt.type === 'pet_dropoff' ? 'text-indigo-600' :
                                        apt.type === 'home_collection' ? 'text-emerald-600' :
                                        apt.type === 'vet_dropoff' ? 'text-orange-600' :
                                        apt.type === 'euthanasia' ? 'text-rose-600' :
                                        apt.type === 'external' ? 'text-gray-600' :
                                        'text-orange-600'
                                      }`}>
                                        {apt.type === 'ashes_collection' ? 'Ashes Collection' :
                                         apt.type === 'prepayment_pickup' ? 'Prepayment Pickup' :
                                         apt.type === 'viewing_room' ? 'Viewing Room' :
                                         apt.type === 'showroom_visit' ? 'Showroom Visit' :
                                         apt.type === 'other_appointment' ? 'Vet Collection' :
                                         apt.type === 'pet_delivery' ? 'Ashes Delivery' :
                                         apt.type === 'pet_dropoff' ? 'Client Drop off' :
                                     apt.type === 'home_collection' ? 'Home Collection' :
                                     apt.type === 'vet_dropoff' ? 'Vet Drop off' :
                                         apt.type === 'euthanasia' ? 'Euthanasia' :
                                         apt.type === 'external' ? (apt.other_type_description || apt.appointment_type) :
                                         'Appointment'}
                                      </div>
                                      {apt.notes && (
                                        <div className="text-xs text-gray-600 italic mt-1">
                                          💬 {apt.notes}
                                        </div>
                                      )}
                                    </div>
                                    <div className="text-2xl">
                                      →
                                    </div>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                    
                    {appointments.length === 0 && (
                      <div className="text-center py-12 text-gray-500">
                        No appointments this week
                      </div>
                    )}
                  </div>
                </>
              )}
              
              {/* Appointment Count */}
              <div className="mt-4 text-center text-sm text-gray-600">
                Total appointments this week: <span className="font-bold text-teal-600">{appointments.length}</span>
              </div>
              
              {/* Pending Tasks Section - Desktop Only - More Compact */}
              <div className="hidden md:block mt-4">
                <PendingTasksSection />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Appointment Details Quick View - Higher z-index */}
      {selectedAppointment && (
        <div className="absolute inset-0 bg-black bg-opacity-75 z-10 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                {selectedAppointment.type === 'ashes_collection' 
                  ? '🏺 Ashes Collection' 
                  : selectedAppointment.type === 'viewing_room'
                  ? '🕯️ Viewing Room'
                  : selectedAppointment.type === 'showroom_visit'
                  ? '🚙 Showroom Visit'
                  : selectedAppointment.type === 'other_appointment'
                  ? '🚐 Vet Collection'
                  : selectedAppointment.type === 'external'
                  ? (selectedAppointment.appointment_type === 'Vet Collection' ? '🚐 Vet Collection' : '👫 External Appointment')
                  : '⭐️ Prepayment Pickup'}
              </h3>
              <button
                onClick={() => setSelectedAppointment(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-3">
              {selectedAppointment.pet_name && (
                <div>
                  <p className="text-sm text-gray-600">Pet Name</p>
                  <p className="font-bold text-gray-900">{selectedAppointment.pet_name}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-600">{selectedAppointment.type === 'external' ? 'Contact Name' : 'Client'}</p>
                <p className="font-medium text-gray-900">{selectedAppointment.type === 'external' ? selectedAppointment.contact_name : selectedAppointment.client_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Phone</p>
                <p className="font-medium text-gray-900">{selectedAppointment.phone_number}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Date & Time</p>
                {!editingAppointment ? (
                  <div className="flex items-center justify-between">
                    <p className="font-bold text-teal-600">
                      {new Date(selectedAppointment.datetime).toLocaleDateString('en-AU', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                      {' at '}
                      {new Date(selectedAppointment.datetime).toLocaleTimeString('en-AU', { 
                        hour: 'numeric', 
                        minute: '2-digit', 
                        hour12: true 
                      })}
                    </p>
                    <button
                      onClick={() => {
                        setEditingAppointment(true);
                        setEditedDatetime(formatDatetimeForInput(selectedAppointment.datetime));
                        setEditedNotes(selectedAppointment.notes || '');
                      }}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      ✏️ Edit
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <input
                      type="datetime-local"
                      value={editedDatetime}
                      onChange={(e) => setEditedDatetime(e.target.value)}
                      className="w-full px-3 py-2 border border-teal-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    />
                    <textarea
                      value={editedNotes}
                      onChange={(e) => setEditedNotes(e.target.value)}
                      placeholder="Add notes (optional)..."
                      rows="3"
                      className="w-full px-3 py-2 border border-teal-300 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setEditingAppointment(false);
                          setEditedNotes('');
                        }}
                        className="flex-1 px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-medium"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveEditedDatetime}
                        className="flex-1 px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-medium"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                )}
              </div>
              {selectedAppointment.type === 'viewing_room' && (
                <div>
                  <p className="text-sm text-gray-600">Duration</p>
                  <p className="font-bold text-purple-600">{selectedAppointment.duration}</p>
                </div>
              )}
              {/* Show notes for all appointment types that have it */}
              {selectedAppointment.notes && (
                <div>
                  <p className="text-sm text-gray-600">Notes</p>
                  <p className="font-medium text-gray-900 text-sm bg-gray-50 p-2 rounded">{selectedAppointment.notes}</p>
                </div>
              )}
              {selectedAppointment.type === 'external' && (
                <>
                  <div>
                    <p className="text-sm text-gray-600">Appointment Type</p>
                    <p className="font-medium text-gray-900">
                      {selectedAppointment.appointment_type}
                      {selectedAppointment.other_type_description && ` - ${selectedAppointment.other_type_description}`}
                    </p>
                  </div>
                </>
              )}
              {selectedAppointment.type === 'prepayment_pickup' && (
                <>
                  <div>
                    <p className="text-sm text-gray-600">Collection Method</p>
                    <p className="font-medium text-gray-900">{selectedAppointment.arrival_method || 'Not specified'}</p>
                  </div>
                  {selectedAppointment.arrival_method === 'Home Pickup' && (
                    <div>
                      <p className="text-sm text-gray-600">Address</p>
                      <p className="font-medium text-gray-900"><AddressLink address={selectedAppointment.address} postcode={selectedAppointment.postcode} testId="appointment-customer-address-link" /></p>
                    </div>
                  )}
                  {selectedAppointment.arrival_method === 'Vet Pickup' && (
                    <div>
                      <p className="text-sm text-gray-600">Vet Clinic</p>
                      <p className="font-medium text-gray-900">{selectedAppointment.vet_clinic}</p>
                    </div>
                  )}
                </>
              )}
            </div>
            
            {/* Quick Actions */}
            <div className="mt-6 space-y-2">
              <div className="flex gap-2">
                {selectedAppointment.type !== 'external' && (
                  <button
                    onClick={() => {
                      setSelectedAppointment(null);
                      onClose();
                      window.location.href = `/pets/${selectedAppointment.id}`;
                    }}
                    className="flex-1 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium"
                  >
                    View Pet Details
                  </button>
                )}
                <button
                  onClick={handleCompleteAppointment}
                  className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium"
                >
                  ✅ Complete
                </button>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium"
                >
                  🗑️ Delete
                </button>
                <button
                  onClick={() => setSelectedAppointment(null)}
                  className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedAppointment && (
        <div className="absolute inset-0 bg-black bg-opacity-75 z-20 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Delete Appointment?
              </h3>
              <p className="text-sm text-gray-600 mb-2">
                Are you sure you want to delete this appointment?
              </p>
              <div className="bg-gray-50 p-3 rounded-lg mb-4 text-left">
                <p className="text-sm font-medium text-gray-900">{selectedAppointment.pet_name || selectedAppointment.contact_name}</p>
                <p className="text-xs text-gray-600">
                  {new Date(selectedAppointment.datetime).toLocaleDateString('en-AU', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  })}
                </p>
              </div>
              <p className="text-xs text-gray-500 mb-4">
                {selectedAppointment.type === 'external' 
                  ? 'This will permanently delete the appointment.'
                  : 'This will remove the appointment from the pet record.'}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAppointment}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Add Appointment Modal */}
      {showAddAppointment && (
        <AddAppointmentModal
          onClose={() => {
            setShowAddAppointment(false);
            setPrefilledDatetime('');
          }}
          onSuccess={() => {
            setShowAddAppointment(false);
            setPrefilledDatetime('');
            fetchAppointments();
            // Refresh pet cards on dashboard to show appointment badges immediately
            if (onAppointmentUpdate) {
              onAppointmentUpdate();
            }
          }}
          prefilledDatetime={prefilledDatetime}
        />
      )}
    </div>
  );
};


export default CalendarView;
